/* eslint-disable @typescript-eslint/no-explicit-any */
import { create } from 'zustand';

interface IModalStore<T extends string, P extends Record<T, any>> {
  /**
   * State
   */
  modals: { type: T; props: P[T] }[];

  /**
   * Actions
   */
  actions: {
    checkIsOpenModal: (type: T) => boolean;
    openModal: <K extends T>(type: K, props: P[K]) => void;
    closeModal: (type?: T) => void;
    closeAll: () => void;
  };
}

/**
 * Deep clone object while preserving functions
 * This detaches large refs but keeps functions intact
 */
function clonePropsWithFunctions<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (typeof obj === 'function') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => clonePropsWithFunctions(item)) as T;
  }

  const cloned = {} as T;
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (typeof value === 'function') {
        // Preserve functions
        (cloned as any)[key] = value;
      } else if (value && typeof value === 'object') {
        // Recursively clone nested objects
        (cloned as any)[key] = clonePropsWithFunctions(value);
      } else {
        // Copy primitive values
        (cloned as any)[key] = value;
      }
    }
  }
  return cloned;
}

export function createModalStoreFactory<T extends string, P extends Record<T, any>>() {
  return create<IModalStore<T, P>>((set, get) => ({
    /**
     * State
     */
    modals: [],

    /**
     * Actions
     */
    actions: {
      checkIsOpenModal: (type) => {
        return get().modals.find((modal) => modal.type === type) !== undefined ? true : false;
      },

      openModal: (type, props) =>
        set((state) => {
          const safeProps = props ? clonePropsWithFunctions(props) : undefined; // detach large refs but preserve functions
          return { modals: [...state.modals, { type, props: safeProps as P[T] }] };
        }),

      closeModal: (type?: T) => {
        set((state) => {
          if (type) {
            return { modals: state.modals.filter((modal) => modal.type !== type) };
          } else {
            return { modals: state.modals.slice(0, -1) };
          }
        });
      },
      closeAll: () => set({ modals: [] }),
    },
  }));
}
